// node_modules
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";

// redux
import {
  useDeleteUserMutation,
  useGetUserListQuery,
  useLazyGetDeletedUserListQuery,
  usePermanentlyDeleteUserMutation,
  useRecoverUserMutation,
  useUpdateUserStateMutation,
} from "../../../redux/services/user";
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";

// context
import { ToastContext } from "../../../App";

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./ChefManagement.module.css";

const ChefManagement = () => {
  const showToast = useContext(ToastContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useMobile();

  const {
    data: chefList,
    isSuccess: isGetChefListSuccess,
    isLoading: isGetChefListLoading,
    isError: isGetChefListError,
  } = useGetUserListQuery(getSubdomain());
  const [
    deleteUser,
    {
      isSuccess: isDeleteChefSuccess,
      isError: isDeleteChefError,
      error: deleteChefError,
    },
  ] = useDeleteUserMutation();
  const [
    updateUserState,
    {
      isSuccess: isUpdateUserStateSuccess,
      isError: isUpdateUserStateError,
      error: updateUserStateError,
    },
  ] = useUpdateUserStateMutation();
  const [
    getDeletedChefs,
    {
      data: deletedChefList,
      isSuccess: isGetDeletedChefsSuccess,
      isError: isGetDeletedChefsError,
    },
  ] = useLazyGetDeletedUserListQuery();
  const [
    recoverChef,
    { isSuccess: isRecoverChefSuccess, isError: isRecoverChefError },
  ] = useRecoverUserMutation();
  const [
    permanentlyDeleteChef,
    {
      isSuccess: isPermanentlyDeleteChefSuccess,
      isError: isPermanentlyDeleteChefError,
    },
  ] = usePermanentlyDeleteUserMutation();

  const [id, setId] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [deleteMode, setDeleteMode] = useState("delete");
  const [data, setData] = useState([]);

  const goCreate = () => navigate("create");
  const goEdit = (rowData) => navigate(`${rowData.id}/edit`);
  const goPassword = (rowData) => navigate(`${rowData.id}/password`);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isGetChefListSuccess) {
      dispatch(setLoading(false));

      setData((prev) => [...prev, ...chefList]);

      getDeletedChefs(getSubdomain());
    }
  }, [isGetChefListSuccess]);

  useEffect(() => {
    if (isGetChefListError) {
      showToast("error", "Chef", "Unable to load chefs list");
      dispatch(setLoading(false));
    }
  }, [isGetChefListError]);

  useEffect(() => {
    let tempData = [];
    if (isGetChefListSuccess) tempData = chefList;
    if (isGetDeletedChefsSuccess) tempData = [...tempData, ...deletedChefList];

    setData(tempData);
  }, [
    isGetChefListSuccess,
    chefList,
    isGetDeletedChefsSuccess,
    deletedChefList,
  ]);

  useEffect(() => {
    if (isDeleteChefSuccess) {
      showToast("success", "Chef", "Chef deleted!");
      setShowDeleteModal(false);
      getDeletedChefs(getSubdomain());
    }
  }, [isDeleteChefSuccess]);

  useEffect(() => {
    if (isDeleteChefError) {
      showToast(
        "error",
        deleteChefError?.status,
        deleteChefError?.data?.message ||
          (deleteChefError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isDeleteChefError]);

  useEffect(() => {
    if (isPermanentlyDeleteChefSuccess) {
      showToast("success", "Chef", "Chef deleted!");
      setShowDeleteModal(false);
      getDeletedChefs(getSubdomain());
    }
  }, [isPermanentlyDeleteChefSuccess]);

  useEffect(() => {
    if (isPermanentlyDeleteChefError) {
      showToast("error", "Chef", "Unable to permanently delete chef");
    }
  }, [isPermanentlyDeleteChefError]);

  useEffect(() => {
    if (isRecoverChefSuccess) {
      showToast("success", "Chef", "Chef recovered!");
      getDeletedChefs(getSubdomain());
    }
  }, [isRecoverChefSuccess]);

  useEffect(() => {
    if (isRecoverChefError) {
      showToast("error", "Chef", "Unable to recover chef");
    }
  }, [isRecoverChefError]);

  useEffect(() => {
    if (isUpdateUserStateSuccess) {
      showToast("success", "User", "User updated!");
    }
  }, [isUpdateUserStateSuccess]);

  useEffect(() => {
    if (isUpdateUserStateError) {
      showToast(
        "error",
        updateUserStateError?.status,
        updateUserStateError?.data?.message ||
          (updateUserStateError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateUserStateError]);

  const openDeleteModal = (rowData) => () => {
    setId(rowData.id);
    setSelectedName(rowData.username);
    setDeleteMode(rowData.isDeleted ? "permanently" : "delete");
    setShowDeleteModal(true);
  };

  const onPermanentlyDelete = () => {
    permanentlyDeleteChef({
      subdomain: getSubdomain(),
      id: id,
    });
  };

  const onDelete = () => {
    deleteUser({
      subdomain: getSubdomain(),
      id: id,
    });
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className={styles.cellSoundWrapper}>
        {rowData.isDeleted ? (
          <Button
            icon="pi pi-refresh"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() =>
              recoverChef({ id: rowData.id, subdomain: getSubdomain() })
            }
          />
        ) : (
          <Button
            icon="pi pi-pencil"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() => goEdit(rowData)}
          />
        )}
        <Button
          icon="pi pi-key"
          className={`p-button-text ${styles.soundBtn}`}
          onClick={() => goPassword(rowData)}
          disabled={rowData.isDeleted}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-text ${styles.deleteBtn}`}
          onClick={openDeleteModal(rowData)}
        />
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={`${styles.header}`}>
        <h2>Chefs</h2>
        <Button className={styles.createBtn} onClick={goCreate}>
          <span>ADD CHEF</span> <i className="pi pi-plus" />
        </Button>
      </div>
      {data &&
        (isMobile ? (
          <div className={styles.cardList}>
            {data.map((chef) => (
              <div key={chef.email} className={styles.cardItem}>
                <div className={styles.cardInfo}>
                  <div className={styles.name}>{chef.username}</div>
                  <div className={styles.subfield}>{chef.email}</div>
                  <div className={styles.subfield}>{chef.phone}</div>
                </div>
                <div className={styles.actions}>
                  {chef.active ? (
                    <button
                      className={styles.cardEditBtn}
                      disabled={chef.isDeleted}
                      onClick={() => {
                        updateUserState({
                          id: chef.id,
                          payload: {
                            active: false,
                          },
                          subdomain: getSubdomain(),
                        });
                      }}
                    >
                      <i className="pi pi-eye" />
                    </button>
                  ) : (
                    <button
                      className={styles.cardEditBtn}
                      disabled={chef.isDeleted}
                      onClick={() => {
                        updateUserState({
                          id: chef.id,
                          payload: {
                            active: true,
                          },
                          subdomain: getSubdomain(),
                        });
                      }}
                    >
                      <i className="pi pi-eye-slash" />
                    </button>
                  )}
                  {chef.isDeleted ? (
                    <button
                      className={styles.cardEditBtn}
                      onClick={() =>
                        recoverChef({ id: chef.id, subdomain: getSubdomain() })
                      }
                    >
                      <i className="pi pi-refresh" />
                    </button>
                  ) : (
                    <button
                      className={styles.cardEditBtn}
                      onClick={() => goEdit(chef)}
                    >
                      <i className="pi pi-pencil" />
                    </button>
                  )}
                  <button
                    className={styles.cardEditBtn}
                    disabled={chef.isDeleted}
                    onClick={() => goPassword(chef)}
                  >
                    <i className="pi pi-key" />
                  </button>
                  <button
                    className={styles.cardDeleteBtn}
                    onClick={openDeleteModal(chef)}
                  >
                    <i className="pi pi-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DataTable value={data} rows={6} loading={isGetChefListLoading}>
            <Column
              field="username"
              header="Name"
              body={(rowData) => {
                return (
                  <div className={styles.cellInsider}>{rowData.username}</div>
                );
              }}
            />

            <Column
              field="email"
              header="Email"
              body={(rowData) => {
                return (
                  <div className={styles.cellInsider}>{rowData.email}</div>
                );
              }}
            />

            <Column
              field="phone"
              header="Phone"
              body={(rowData) => {
                return (
                  <div className={styles.cellInsider}>{rowData.phone}</div>
                );
              }}
            />
            <Column
              field="active"
              header={() => {
                return <div>Active</div>;
              }}
              body={(rowData) => {
                return (
                  <div>
                    {rowData.active ? (
                      <Button
                        icon="pi pi-eye"
                        className={`p-button-text ${styles.activeBtn}`}
                        disabled={rowData.isDeleted}
                        onMouseUp={() => {
                          updateUserState({
                            id: rowData.id,
                            payload: {
                              active: false,
                            },
                            subdomain: getSubdomain(),
                          });
                        }}
                      />
                    ) : (
                      <Button
                        icon="pi pi-eye-slash"
                        className={`p-button-text ${styles.activeBtn}`}
                        disabled={rowData.isDeleted}
                        onMouseUp={() => {
                          updateUserState({
                            id: rowData.id,
                            payload: {
                              active: true,
                            },
                            subdomain: getSubdomain(),
                          });
                        }}
                      />
                    )}
                  </div>
                );
              }}
            />

            <Column
              body={actionBodyTemplate}
              header="Actions"
              style={{ width: "175px" }}
            />
          </DataTable>
        ))}

      <Dialog
        closable={false}
        header={
          <div style={{ width: "100%", textAlign: "center" }}>
            Are You Sure?
          </div>
        }
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        blockScroll
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={
                deleteMode === "permanently" ? onPermanentlyDelete : onDelete
              }
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>
            You want to {deleteMode === "permanently" ? "permanently" : ""}{" "}
            delete the chef: <strong>{selectedName}</strong>.
          </span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>
    </div>
  );
};

export default ChefManagement;
